import React, { useEffect, useRef, useState, useCallback } from "react";
import "./NetworkErrorModal.scss";

/**
 * NetworkErrorModal
 *
 * Full-screen blocking modal shown when the app loses connectivity or an
 * API call fails with a network error. Auto-retries on a visible countdown
 * ring built into the retry button itself, and immediately retries the
 * moment the browser regains a connection.
 *
 * Props:
 * - open            {boolean}  whether the modal is visible
 * - onRetry         {function} async () => void — called on manual or auto retry
 * - autoRetrySeconds{number}   seconds between automatic retries (default 15)
 * - dismissible     {boolean}  show a close affordance (default false — network
 *                              errors are usually blocking)
 * - onDismiss       {function} called when dismissed (only used if dismissible)
 * - title           {string}
 * - message         {string}
 */
const NetworkErrorModal = ({
  open,
  onRetry,
  autoRetrySeconds = 15,
  dismissible = false,
  onDismiss,
  title = "Unstable Internet Connection",
  message = "We can't reach our servers right now. Check your connection — we'll try again automatically.",
}) => {
  const [isRetrying, setIsRetrying] = useState(false);
  const [progress, setProgress] = useState(0); // 0 -> 1
  const [isOnline, setIsOnline] = useState(
    typeof navigator !== "undefined" ? navigator.onLine : true
  );
  const rafRef = useRef(null);
  const startRef = useRef(null);
  const retryingRef = useRef(false);

  const runRetry = useCallback(
    async (auto = false, isOnlineEvent = false) => {
      if (retryingRef.current) return;
      retryingRef.current = true;
      setIsRetrying(true);
      setProgress(0);
      try {
        await onRetryRef.current?.(auto, isOnlineEvent);
      } catch (err) {
        // swallow — the modal simply stays open / the parent will re-show it
        // if the retry fails, the caller's onRetry is responsible for that.
      } finally {
        retryingRef.current = false;
        setIsRetrying(false);
        startRef.current = null;
      }
    },
    [] // No dependencies - uses ref instead
  );

  // Store the latest onRetry in a ref to avoid recreating runRetry
  const onRetryRef = useRef(onRetry);
  useEffect(() => {
    onRetryRef.current = onRetry;
  }, [onRetry]);

  /* eslint-disable react-hooks/exhaustive-deps */
  // Countdown ring animation loop
  useEffect(() => {
    if (!open || isRetrying) {
      setProgress(0);
      return undefined;
    }

    const durationMs = autoRetrySeconds * 1000;
    startRef.current = null;

    const tick = (t) => {
      if (startRef.current === null) startRef.current = t;
      const elapsed = t - startRef.current;
      const pct = Math.min(elapsed / durationMs, 1);
      setProgress(pct);

      if (pct >= 1) {
        runRetry(true);
        return;
      }
      rafRef.current = requestAnimationFrame(tick);
    };

    rafRef.current = requestAnimationFrame(tick);
    return () => {
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
      startRef.current = null;
    };
  }, [open, isRetrying, autoRetrySeconds]);

  // React immediately when the browser regains connectivity
  useEffect(() => {
    const handleOnline = () => {
      setIsOnline(true);
      runRetry(true, true); // Pass true for isOnlineEvent
    };
    const handleOffline = () => setIsOnline(false);

    window.addEventListener("online", handleOnline);
    window.addEventListener("offline", handleOffline);
    return () => {
      window.removeEventListener("online", handleOnline);
      window.removeEventListener("offline", handleOffline);
    };
  }, []);
  /* eslint-enable react-hooks/exhaustive-deps */

  // Escape key for dismissible variant
  useEffect(() => {
    if (!open || !dismissible) return undefined;
    const handleKey = (e) => {
      if (e.key === "Escape") onDismiss?.();
    };
    window.addEventListener("keydown", handleKey);
    return () => window.removeEventListener("keydown", handleKey);
  }, [open, dismissible, onDismiss]);

  if (!open) return null;

  const secondsLeft = Math.max(0, Math.ceil(autoRetrySeconds * (1 - progress)));
  const circumference = 2 * Math.PI * 21; // r=21
  const dashOffset = circumference * (1 - progress);

  return (
    <div
      className="network-error-modal__overlay"
      role="alertdialog"
      aria-modal="true"
      aria-labelledby="network-error-title"
      aria-describedby="network-error-message"
    >
      <div className="network-error-modal__card">
        {dismissible && (
          <button
            type="button"
            className="network-error-modal__close"
            aria-label="Dismiss"
            onClick={onDismiss}
          >
            <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
              <path
                d="M1 1L13 13M13 1L1 13"
                stroke="currentColor"
                strokeWidth="1.6"
                strokeLinecap="round"
              />
            </svg>
          </button>
        )}

        <div className="network-error-modal__glyph" aria-hidden="true">
          <span className="network-error-modal__ping network-error-modal__ping--1" />
          <span className="network-error-modal__ping network-error-modal__ping--2" />
          <div className="network-error-modal__glyph-core">
            <svg width="30" height="30" viewBox="0 0 30 30" fill="none">
              <path
                d="M6 12.5C10.9 8.2 19.1 8.2 24 12.5"
                stroke="currentColor"
                strokeWidth="2.2"
                strokeLinecap="round"
              />
              <path
                d="M10.2 16.8C12.7 14.7 17.3 14.7 19.8 16.8"
                stroke="currentColor"
                strokeWidth="2.2"
                strokeLinecap="round"
                opacity="0.55"
              />
              <circle cx="15" cy="21.5" r="1.6" fill="currentColor" />
              <path
                d="M4 5L26 25"
                stroke="currentColor"
                strokeWidth="2.2"
                strokeLinecap="round"
              />
            </svg>
          </div>
        </div>

        <h2 id="network-error-title" className="network-error-modal__title">
          {title}
        </h2>
        <p id="network-error-message" className="network-error-modal__message">
          {message}
        </p>

        <div className="network-error-modal__status">
          <span
            className={`network-error-modal__status-dot ${
              isOnline ? "network-error-modal__status-dot--online" : ""
            }`}
          />
          {isOnline ? "Network detected — retrying" : "Unstable or No Network Detected"}
        </div>

        <button
          type="button"
          className="network-error-modal__retry"
          onClick={() => runRetry(false)}
          disabled={isRetrying}
        >
          <svg
            className="network-error-modal__retry-ring"
            width="48"
            height="48"
            viewBox="0 0 48 48"
          >
            <circle
              className="network-error-modal__retry-ring-track"
              cx="24"
              cy="24"
              r="21"
              fill="none"
              strokeWidth="2"
            />
            {!isRetrying && (
              <circle
                className="network-error-modal__retry-ring-progress"
                cx="24"
                cy="24"
                r="21"
                fill="none"
                strokeWidth="2"
                strokeDasharray={circumference}
                strokeDashoffset={dashOffset}
                transform="rotate(-90 24 24)"
              />
            )}
          </svg>

          <span className="network-error-modal__retry-inner">
            {isRetrying ? (
              <span className="network-error-modal__spinner" />
            ) : (
              <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                <path
                  d="M13.5 8A5.5 5.5 0 1 1 11.9 4.3M13.5 2v3.2h-3.2"
                  stroke="currentColor"
                  strokeWidth="1.6"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
            )}
          </span>
        </button>

        <p className="network-error-modal__retry-caption">
          {isRetrying ? "Retrying now…" : `Retrying automatically in ${secondsLeft}s — or tap to retry now`}
        </p>
      </div>
    </div>
  );
};

export default NetworkErrorModal;